import { setDocumentTitle } from '@common/util';
import { updateLocation } from '@/helpscout';

import { TrackingEventType, TrackingPropertyMapped } from './tracking/trackingTypes';
import { RequireAtLeastOne } from 'type-fest';
import { Plugin } from 'vue';

export type AfterRouteEnterData = RequireAtLeastOne<
  {
    documentTitle?: string;
    trackEvent?: TrackingEventType;
    trackingProperties?: TrackingPropertyMapped;
  },
  'documentTitle' | 'trackEvent'
>;

/**
 * Plugin that allows that defines a afterRouteEnter event
 * The handler can return document title and and event to track, which will be handled
 * by the plugin. The data is only set once.
 *
 * Previously this plugin actually used the beforeRouteEnter option to only set the
 * created listener on route components but this doesn't work in vue 3 anymore:
 *
 * https://github.com/vuejs/router/issues/454
 *
 * Can be used in two ways:
 * - with an component option called afterRouteEnter. For static titles
 * - with a computed property called afterRouteEnter. For dynamic titles
 */
export const afterRouteEnterPlugin: Plugin = {
  install(app, _options) {
    app.mixin({
      created() {
        if (this.$options.afterRouteEnter) {
          // afterRouteEnter is component option
          const result = this.$options.afterRouteEnter.call(this);
          if (result?.documentTitle) {
            // Returning title is not required
            setDocumentTitle(result.documentTitle);
          }
          updateLocation();
          if (result?.trackEvent) {
            this.$tracker.track(result.trackEvent, result.trackingProperties || {});
          }
        } else if (typeof this.afterRouteEnter === 'object') {
          // afterRouteEnter is computed
          const unwatch = this.$watch('afterRouteEnter', (newVal: AfterRouteEnterData) => {
            if (newVal !== null) {
              unwatch();
              if (newVal.documentTitle) {
                // Returning title is not required
                setDocumentTitle(newVal.documentTitle);
              }
              updateLocation();
              if (newVal.trackEvent) {
                this.$tracker.track(newVal.trackEvent, newVal.trackingProperties || {});
              }
            }
          });
        }
      },
    });
  },
};
